const AES_KEY_BYTES = 32;
const AES_GCM_IV_BYTES = 12;
const AES_GCM_TAG_LENGTH_BITS = 128;
const RSA_OAEP_HASH = "SHA-256";

function base64ToUint8Array(base64) {
  const cleaned = String(base64 || "")
    .replace(/-----BEGIN PUBLIC KEY-----/g, "")
    .replace(/-----END PUBLIC KEY-----/g, "")
    .replace(/\s+/g, "");
  const raw = window.atob(cleaned);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) {
    bytes[i] = raw.charCodeAt(i);
  }
  return bytes;
}

function uint8ArrayToBase64(bytes) {
  let binary = "";
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

async function importRsaPublicKey(publicKeyPemOrBase64) {
  const keyBytes = base64ToUint8Array(publicKeyPemOrBase64);
  return window.crypto.subtle.importKey(
    "spki",
    keyBytes,
    {
      name: "RSA-OAEP",
      hash: RSA_OAEP_HASH,
    },
    false,
    ["encrypt"],
  );
}

async function encryptBinaryWithAesGcm(fileBytes) {
  const rawAesKey = window.crypto.getRandomValues(new Uint8Array(AES_KEY_BYTES));
  const iv = window.crypto.getRandomValues(new Uint8Array(AES_GCM_IV_BYTES));
  const aesKey = await window.crypto.subtle.importKey(
    "raw",
    rawAesKey,
    { name: "AES-GCM" },
    false,
    ["encrypt"],
  );
  const ciphertext = await window.crypto.subtle.encrypt(
    { name: "AES-GCM", iv, tagLength: AES_GCM_TAG_LENGTH_BITS },
    aesKey,
    fileBytes,
  );
  return { rawAesKey, iv, ciphertext };
}

export async function encryptFileForUpload(file, publicKeyPemOrBase64) {
  if (!window.crypto || !window.crypto.subtle) {
    throw new Error("Browser crypto APIs are unavailable.");
  }
  if (!file) {
    throw new Error("No file provided for encryption.");
  }
  if (!publicKeyPemOrBase64) {
    throw new Error("Upload encryption key is not configured.");
  }

  const fileBytes = await file.arrayBuffer();
  const { rawAesKey, iv, ciphertext } = await encryptBinaryWithAesGcm(fileBytes);
  const rsaPublicKey = await importRsaPublicKey(publicKeyPemOrBase64);
  const wrappedKey = await window.crypto.subtle.encrypt(
    { name: "RSA-OAEP" },
    rsaPublicKey,
    rawAesKey,
  );

  return {
    encryptedBlob: new Blob([ciphertext], { type: "application/octet-stream" }),
    manifest: {
      version: "enc-v1",
      cipher: "AES-256-GCM",
      keyWrap: "RSA-OAEP-SHA256",
      iv: uint8ArrayToBase64(iv),
      wrappedKey: uint8ArrayToBase64(new Uint8Array(wrappedKey)),
      originalName: file.name,
      originalType: file.type || "application/octet-stream",
      originalSize: file.size,
    },
  };
}
